import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ICompanyLogoData } from '../interfaces/company-logo-data.interface';
import { CompanyService } from '../company.service.service';

@Component({
  selector: 'app-company-logo-dialog',
  templateUrl: './company-logo-dialog.component.html',
  styleUrl: './company-logo-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule
  ]
})
export class CompanyLogoDialogComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  previewUrl: string | null = null;
  isUploading = false;
  isPreviewMode = false;
  dragOver = false;

  constructor(
    public dialogRef: MatDialogRef<CompanyLogoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICompanyLogoData,
    private companyService: CompanyService,
    private snackBar: MatSnackBar
  ) {
    this.isPreviewMode = !this.data.currentLogoUrl;
    this.previewUrl = this.data.currentLogoUrl || null;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.handleFile(input.files[0]);
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  private handleFile(file: File): void {
    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      this.snackBar.open('Por favor selecciona un archivo de imagen válido', 'Cerrar', {
        duration: 3000
      });
      return;
    }

    // Validar tamaño (máximo 20MB)
    if (file.size > 20 * 1024 * 1024) {
      this.snackBar.open('El archivo es demasiado grande. Máximo 20MB permitido', 'Cerrar', {
        duration: 3000
      });
      return;
    }

    this.selectedFile = file;

    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewUrl = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  openFileSelector(): void {
    this.fileInput.nativeElement.click();
  }

  updateLogo(): void {
    if (!this.selectedFile) {
      this.snackBar.open('Por favor selecciona un archivo', 'Cerrar', {
        duration: 3000
      });
      return;
    }

    this.isUploading = true;

    this.companyService.updateCompanyLogo(this.data.companyId, this.selectedFile)
      .subscribe({
        next: (response) => {
          this.isUploading = false;
          this.snackBar.open('Logo actualizado exitosamente', 'Cerrar', {
            duration: 3000
          });
          this.dialogRef.close({ success: true, logoUrl: response.logoUrl });
      
        },
        error: (error) => {
          this.isUploading = false;
          this.snackBar.open(
            error.error?.message || 'Error al actualizar el logo',
            'Cerrar',
            { duration: 3000 }
          );
        }
      });
  }

  removeLogo(): void {
    // Para futuro: implementar eliminación de logo
    this.selectedFile = null;
    this.previewUrl = null;
  }

  close(): void {
    this.dialogRef.close();
  }
}